import React, { useState } from 'react';
import Paragraph from 'src/elements/typography/paragraph';
import { navigateToCohost } from 'src/functions/communityNameGenerator/navigateToCohost';
import { palette } from 'src/styles/colors';
import {
  InputSectionContainer,
  InputWrapper,
  Input,
  WidgetContainer,
  WidgetTitleSection,
  DisclaimerSection,
  PaperAirplaneButton,
} from './AiNameGeneratorWidget.styles';
import Heading from 'src/elements/typography/heading';
import PaperPlane from 'src/images/PaperPlane';
import { Link } from 'gatsby';
import { AiNameGeneratorWidgetContentfulModel } from 'src/api/models/AiNameGeneratorWidget';
import { InlineNameGeneratorThemesColors } from '../../InlineNameGeneratorArticle/InlineNameGenerator/InlineNameGenerator.styles';

interface AiNameGeneratorWidgetProps {
  title: string;
  description: string;
  inputPlaceholder: string;
  helperText: string;
  generationMode?: string;
  theme?: AiNameGeneratorWidgetContentfulModel['theme'];
}

const AiNameGeneratorWidget = ({
  title,
  description,
  inputPlaceholder,
  helperText,
  generationMode = 'generator',
  theme = 'Default',
}: AiNameGeneratorWidgetProps): JSX.Element => {
  const [idealMember, setIdealMember] = useState<string>('');
  const [inputError, setInputError] = useState<string>(null);

  const handleIdealMemberSubmit = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (idealMember?.length > 60) {
      setInputError('Must be less than 60 characters');
    } else if (idealMember?.length <= 3) {
      setInputError('Please enter more about who you want to bring together');
    } else {
      setInputError(null);

      navigateToCohost({
        idealMember,
        event,
        mode: generationMode,
        sectionLabel: `${generationMode}-widget`,
      });
    }
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleIdealMemberSubmit(event as unknown as React.MouseEvent<HTMLButtonElement, MouseEvent>);
    }
  };

  const renderGoButton = (text: string) => (
    <PaperAirplaneButton
      onClick={handleIdealMemberSubmit}
      backgroundColor={InlineNameGeneratorThemesColors[theme]?.buttonBackground}
      borderColor={InlineNameGeneratorThemesColors[theme]?.buttonBorderColor}
    >
      <Paragraph
        text={text}
        color='white'
        size='1rem'
        weight='light'
        align='center'
      />
      <PaperPlane width='20px' />
    </PaperAirplaneButton>
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => setIdealMember(event.target.value);

  return (
    <WidgetContainer backgroundColor={InlineNameGeneratorThemesColors[theme]?.background}>
      <WidgetTitleSection>
        <Heading
          text={title}
          standardSize='3.375'
          align='center'
          className='widget-title'
          color={InlineNameGeneratorThemesColors[theme]?.text}
        />
        <Paragraph
          text={description}
          standardSize='1.125'
          align='center'
          weight='light'
          className='widget-description'
          color={InlineNameGeneratorThemesColors[theme]?.text}
        />
      </WidgetTitleSection>
      <InputSectionContainer>
        {inputError && (
          <Paragraph
            text={inputError}
            size='.8rem'
            align='left'
            color={palette.softRed}
            weight='light'
          />
        )}
        <InputWrapper>
          <Input
            onChange={handleInputChange}
            type='text'
            name='idealMember'
            value={idealMember}
            maxLength={60}
            placeholder={inputPlaceholder}
            onKeyDown={handleInputKeyDown}
          />

          {/* Hide on mobile */}
          {renderGoButton('Generate')}
        </InputWrapper>
        {/* Show on mobile */}
        {renderGoButton('Generate')}
        <Paragraph
          text={helperText}
          size='0.875rem'
          sizeSm='0.625rem'
          align='center'
          weight='light'
          className='widget-helper-text'
          color={InlineNameGeneratorThemesColors[theme]?.text}
        />
      </InputSectionContainer>
      <DisclaimerSection>
        <Paragraph
          size='0.75rem'
          align='center'
          weight='light'
          className='widget-disclaimer'
          color={InlineNameGeneratorThemesColors[theme]?.text}
        >
          The names generated by Mighty Co-Host™ are examples only and may be used by other businesses or subject to
          third-party rights. For more information, check our <Link to='/terms-of-use'>Terms</Link>.
        </Paragraph>
      </DisclaimerSection>
    </WidgetContainer>
  );
};

export default AiNameGeneratorWidget;
